import { Config } from '@config';
import { R } from '@core/utils/r';
import { AuthConfig, isRequestResponse, Request, RequestEvent, RequestResponse } from '@core/utils/request';
import { Session } from '@modules/auth/session';
import moment from 'moment-timezone';

/** Guards against a burst of parallel 401s each kicking off their own logout. */
let endingSession = false;

/**
 * Set by EnsureUserIsActive and by the unauthenticated handler. A bare 401 is not enough to go
 * on: several endpoints abort with 401 for reasons that have nothing to do with the session
 * (an Unsplash outage in ImageFinderController, an invalid Nylas key in SettingsController),
 * and signing the user out of the whole app for those would be a regression.
 */
const SESSION_INVALID_HEADER = 'X-Session-Invalid';

function isSessionInvalid(error: any): boolean {
  const response: RequestResponse | undefined = error && error.__response;
  if (!response || !response.headers) return false;
  return response.headers.get(SESSION_INVALID_HEADER) === '1';
}

/**
 * A signed-in user whose token stopped being accepted — revoked, expired, or the account was
 * deactivated by an administrator — is dropped back to the login screen instead of being left
 * on a page where every request silently fails. The reason is surfaced by the login endpoint
 * on their next sign-in attempt.
 *
 * Deliberately no FlashMessage here: that module imports this one, and calling it would make
 * the cycle load-bearing.
 */
function handleUnauthorized(event: RequestEvent) {
  if (event.type !== 'error' || event.statusCode !== 401) return;
  if (!isSessionInvalid(event.transformedError)) return;
  if (!Session.isAuthenticated || endingSession) return;

  endingSession = true;

  Session.logout().subscribe({
    complete: () => {
      endingSession = false;
    },
    error: () => {
      endingSession = false;
    },
  });
}

export const Endpoint = new Request({
  urlMappings: () => ({
    '@gc': Config.GC_API_URL,
    '@front': Config.GC_URL,
    '@ngrok': 'https://s98.ngrok.io/api',
  }),

  authMappings: () => ({
    '@gc': getGrandCentralAuth,
    '@front': getGrandCentralAuth,
    '@ngrok': getGrandCentralAuth,
  }),

  transformResponse(response: RequestResponse) {
    return response.json();
  },

  async transformError(response: RequestResponse) {
    if (!isRequestResponse(response)) return response;

    try {
      return await wrapEndpointError(response);
    } catch (e) {
      return response;
    }
  },

  onEvent: handleUnauthorized,

  headers: {
    Accept: 'application/json',
    timezone: moment.tz.guess(),
  },
});

export function getGrandCentralAuth(token = Session.token): AuthConfig | null {
  if (!token) return null;
  return {
    type: 'header',
    name: 'Authorization',
    value: `Bearer ${token}`,
  };
}

export async function wrapEndpointError(response: RequestResponse) {
  const data = await response.json();

  let message: string | null = null;

  if (data) {
    if (R.isString(data.message)) {
      message = data.message;
    } else if (R.isString(data.error)) {
      message = data.error;
    }
  }

  const error = {};

  (error as any).__endpoint_error = true;
  (error as any).__response = response;
  (error as any).__message = message;

  return error;
}

export function getEndpointResponseErrorMessage(error: any) {
  if (!error || error.__endpoint_error !== true) return null;
  return error.__message;
}

export function getEndpointResponseStatusCode(error: any) {
  if (!error || error.__endpoint_error !== true) return null;
  return (error.__response as RequestResponse).status;
}
